import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { NftItemTileMedia } from 'dibs-nft-item-tile/exports/NftItemTileMedia.ts';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { NftAvatarCircle } from 'dibs-crypto-utils/exports/NftAvatarCircle.ts';
import { useDeviceType } from '../../../../nftHeader/hooks/useDeviceType.tsx';
import { type NftFeaturedCreatorTile_nftProfile$key } from './__generated__/NftFeaturedCreatorTile_nftProfile.graphql.ts';

import classNames from 'classnames';
import styles from './NftFeaturedCreatorTile.scss';

type Props = {
    nftProfile: NftFeaturedCreatorTile_nftProfile$key | null | undefined;
};

export const NftFeaturedCreatorTile: FC<Props> = ({ nftProfile: nftProfileRef }) => {
    const { isMobile } = useDeviceType();
    const nftProfile = useFragment(
        graphql`
            fragment NftFeaturedCreatorTile_nftProfile on NftFeaturedCreatorsProfileType {
                username
                featuredItems {
                    ...NftItemTileMedia_item
                }
                publicProfile {
                    walletAddress {
                        ...NftAvatarCircle_address
                        address
                        publicProfileUrl
                    }
                    profileName
                }
                totalCreations
            }
        `,
        nftProfileRef
    );

    const { username, totalCreations, publicProfile, featuredItems } = nftProfile || {};

    let featuredItemMedia;

    if (isMobile) {
        featuredItemMedia = (
            <div className={styles.bannerContainer}>
                <div className={styles.imageContainer}>
                    <NftItemTileMedia
                        item={featuredItems?.[0] || null}
                        animate={false}
                        isMobile
                        showIcon={false}
                    />
                </div>
                <NftAvatarCircle
                    address={publicProfile?.walletAddress}
                    wrapperClassName={styles.avatarContainer}
                    loading="lazy"
                    avatarSize={70}
                />
            </div>
        );
    } else {
        featuredItemMedia = (
            <div className={styles.gridContainer}>
                {featuredItems?.map((item, index) => {
                    return (
                        <div
                            className={classNames({
                                [styles.item1]: index === 0,
                                [styles.item2]: index === 1,
                                [styles.item3]: index === 2,
                            })}
                            key={`grid-${index}`}
                        >
                            <NftItemTileMedia
                                item={item || null}
                                animate={false}
                                isMobile={false}
                                showIcon={false}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <Link
            href={publicProfile?.walletAddress?.publicProfileUrl || ''}
            buyerLinkType="standardLinkNoEffect"
            className={styles.container}
        >
            {featuredItemMedia}
            <div className={styles.profileContainer}>
                <p className={styles.displayName}>
                    {publicProfile?.profileName ? publicProfile.profileName : username}
                </p>
                <div className={styles.usernameItemCountContainer}>
                    <p>{`@${username}`}</p>
                    {totalCreations && publicProfile?.profileName && <span>|</span>}
                    {totalCreations && (
                        <p className={styles.totalCreations}>
                            <FormattedMessage
                                id="nft.creators.featuredCreatorTile.totalCreations"
                                defaultMessage="{totalCreations, plural, one {# Creation} other {# Creations}}"
                                values={{
                                    totalCreations,
                                }}
                            />
                        </p>
                    )}
                </div>
            </div>
        </Link>
    );
};
